import { useMemo } from "react";
import { useTable, useSortBy } from "react-table";
import { ITableColumn } from "../Table/TableColumn";
import { DefaultCellForColumn } from "../Table/DefaultCellForColumn";
import { DefaultHeaderForColumn } from "../Table/DefaultHeaderForColumn";
import { TableWrapper, TableLoader, TableHeaderCell, Align } from "../Table/style";
import { IPnLRow } from "../../utils/pnl";
import { IFundingData } from "../../hooks/api/funding/useFundingData";
import { COLORS, TEXT_COLORS } from "../../constants/design/colors";
import { formatAmount } from "../../utils/strings";
import { getValueColor } from "./utils";

interface ICombinedRow {
  Asset: string;
  PnL: number;
  TotalFees: number; // Positive = cost, Negative = rebate
  NetPnL: number;
  TotalBuyVolume: number;
  TotalSellVolume: number;
  AevoFunding: number;
  BinanceFunding: number;
  BybitFunding: number;
  NetFunding: number;
  NetTotal: number; // Net PnL + Net Funding
}

interface IGeneralTableProps {
  pnlData: IPnLRow[];
  fundingData: IFundingData[];
  isLoadingPnL: boolean;
  isLoadingFunding: boolean;
}

export function GeneralTable({
  pnlData,
  fundingData,
  isLoadingPnL,
  isLoadingFunding,
}: IGeneralTableProps) {
  // Combine PnL and Funding data by asset
  const combinedData = useMemo<ICombinedRow[]>(() => {
    const combined: Record<string, ICombinedRow> = {};

    // Process PnL data
    if (pnlData) {
      pnlData.forEach((row) => {
        const asset = row.InstrumentName.split("-")[0];
        if (!combined[asset]) {
          combined[asset] = {
            Asset: asset,
            PnL: 0,
            TotalFees: 0,
            NetPnL: 0,
            TotalBuyVolume: 0,
            TotalSellVolume: 0,
            AevoFunding: 0,
            BinanceFunding: 0,
            BybitFunding: 0,
            NetFunding: 0,
            NetTotal: 0,
          };
        }
        combined[asset].PnL += row.PnL;
        combined[asset].TotalFees += row.TotalFees;
        combined[asset].NetPnL += row.NetPnL;
        combined[asset].TotalBuyVolume += row.TotalBuyVolume;
        combined[asset].TotalSellVolume += row.TotalSellVolume;
      });
    }

    // Process Funding data
    if (fundingData) {
      fundingData.forEach((row) => {
        const asset = row.Pair.split("-")[0];
        if (!combined[asset]) {
          combined[asset] = {
            Asset: asset,
            PnL: 0,
            TotalFees: 0,
            NetPnL: 0,
            TotalBuyVolume: 0,
            TotalSellVolume: 0,
            AevoFunding: 0,
            BinanceFunding: 0,
            BybitFunding: 0,
            NetFunding: 0,
            NetTotal: 0,
          };
        }
        combined[asset].AevoFunding += row.Aevo_Funding || 0;
        combined[asset].BinanceFunding += row.Binance_Funding || 0;
        combined[asset].BybitFunding += row.Bybit_Funding || 0;
        combined[asset].NetFunding += row.Net_Funding || 0;
      });
    }

    return Object.values(combined)
      .map((row) => ({
        ...row,
        NetTotal: row.NetPnL + row.NetFunding,
      }))
      .sort((a, b) => a.Asset.localeCompare(b.Asset));
  }, [pnlData, fundingData]);

  const columns: ITableColumn<ICombinedRow>[] = useMemo(
    () => [
      {
        title: "Asset",
        accessor: "Asset",
        align: "left",
        Cell: ({ value }: { value: string }) => (
          <div
            style={{
              backgroundColor: COLORS.blue.two,
              color: COLORS.blue.one,
              padding: "4px 12px",
              borderRadius: "12px",
              display: "inline-block",
              fontSize: "12px",
              fontWeight: 500,
            }}
          >
            {value}
          </div>
        ),
      },
      {
        title: "PnL",
        accessor: "PnL",
        align: "right",
        Cell: ({ value }: { value: number }) => {
          const pnlValue = Number(value);
          return (
            <Align
              align="right"
              style={{
                color: getValueColor(pnlValue),
              }}
            >
              {pnlValue > 0 ? "+" : ""}
              {formatAmount(pnlValue)}
            </Align>
          );
        },
      },
      {
        title: "Fees",
        accessor: "TotalFees",
        align: "right",
        Cell: ({ value }: { value: number }) => {
          const feesValue = Number(value);
          // Negative fees = rebate (good, green), Positive fees = cost (bad, red)
          const displayColor = feesValue < 0 ? getValueColor(Math.abs(feesValue)) : getValueColor(-feesValue);
          return (
            <Align
              align="right"
              style={{
                color: displayColor,
              }}
            >
              {formatAmount(feesValue)}
            </Align>
          );
        },
      },
      {
        title: "Net PnL",
        accessor: "NetPnL",
        align: "right",
        Cell: ({ value }: { value: number }) => {
          const netPnLValue = Number(value);
          return (
            <Align
              align="right"
              style={{
                color: getValueColor(netPnLValue),
                fontWeight: "bold",
              }}
            >
              {netPnLValue > 0 ? "+" : ""}
              {formatAmount(netPnLValue)}
            </Align>
          );
        },
      },
      {
        title: "Buy Volume",
        accessor: "TotalBuyVolume",
        align: "right",
        Cell: ({ value }: { value: number }) => <Align align="right">{formatAmount(value)}</Align>,
      },
      {
        title: "Sell Volume",
        accessor: "TotalSellVolume",
        align: "right",
        Cell: ({ value }: { value: number }) => <Align align="right">{formatAmount(value)}</Align>,
      },
      {
        title: "Aevo Funding",
        accessor: "AevoFunding",
        align: "right",
        Cell: ({ value }: { value: number }) => {
          const fundingValue = Number(value);
          return (
            <Align
              align="right"
              style={{
                color: getValueColor(fundingValue),
              }}
            >
              {fundingValue > 0 ? "+" : ""}
              {formatAmount(fundingValue)}
            </Align>
          );
        },
      },
      {
        title: "Binance Funding",
        accessor: "BinanceFunding",
        align: "right",
        Cell: ({ value }: { value: number }) => {
          const fundingValue = Number(value);
          return (
            <Align
              align="right"
              style={{
                color: getValueColor(fundingValue),
              }}
            >
              {fundingValue > 0 ? "+" : ""}
              {formatAmount(fundingValue)}
            </Align>
          );
        },
      },
      {
        title: "Bybit Funding",
        accessor: "BybitFunding",
        align: "right",
        Cell: ({ value }: { value: number }) => {
          const fundingValue = Number(value);
          return (
            <Align
              align="right"
              style={{
                color: getValueColor(fundingValue),
              }}
            >
              {fundingValue > 0 ? "+" : ""}
              {formatAmount(fundingValue)}
            </Align>
          );
        },
      },
      {
        title: "Net Funding",
        accessor: "NetFunding",
        align: "right",
        Cell: ({ value }: { value: number }) => {
          const netFundingValue = Number(value);
          return (
            <Align
              align="right"
              style={{
                color: getValueColor(netFundingValue),
                fontWeight: "bold",
              }}
            >
              {netFundingValue > 0 ? "+" : ""}
              {formatAmount(netFundingValue)}
            </Align>
          );
        },
      },
      {
        title: "Net Total",
        accessor: "NetTotal",
        align: "right",
        Cell: ({ value }: { value: number }) => {
          const netTotalValue = Number(value);
          return (
            <Align
              align="right"
              style={{
                color: getValueColor(netTotalValue),
                fontWeight: "bold",
                fontSize: "14px",
              }}
            >
              {netTotalValue > 0 ? "+" : ""}
              {formatAmount(netTotalValue)}
            </Align>
          );
        },
      },
    ],
    []
  );

  const isLoading = isLoadingPnL || isLoadingFunding;

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data: combinedData,
        defaultColumn: {
          Header: DefaultHeaderForColumn,
          Cell: DefaultCellForColumn,
        } as any,
        autoResetSortBy: false,
        initialState: {
          sortBy: [
            {
              id: "Asset",
              desc: false,
            },
          ],
        },
      },
      useSortBy
    );

  return (
    <TableWrapper>
      <TableLoader isLoading={isLoading} />
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column: any) => (
                <TableHeaderCell
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  style={{ width: column.width }}
                >
                  {column.render("Header")}
                </TableHeaderCell>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            const { ...rowProps } = row.getRowProps();
            return (
              <tr {...rowProps}>
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                ))}
              </tr>
            );
          })}
          {!isLoading && rows.length === 0 && (
            <tr>
              <td colSpan={columns.length} style={{ textAlign: "center" }}>
                No data available for the selected period.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </TableWrapper>
  );
}
